import api from "@/services/api";

/**
 * Servicio para manejar suscripciones, planes y módulos del usuario
 * Basado en las APIs del sistema de planes y suscripciones
 */
export default {
    /**
     * Obtiene la suscripción efectiva del usuario autenticado
     * Incluye información completa del plan y sus módulos
     * @returns {Promise} Respuesta con suscripción activa o mensaje si no tiene
     */
    async getEffectiveSubscription() {
        const response = await api.get("/plan-subscriptions/current");

        // El backend devuelve {data: {subscription: {...}}, status: 200}
        // Necesitamos extraer response.data.data
        const result = response.data.data || response.data;

        return result;
    },

    /**
     * Obtiene los módulos activos de la aplicación del usuario
     * @param {Object} params - Parámetros de paginación
     * @param {Number} params.per_page - Resultados por página (default: 20)
     * @param {Number} params.page - Página actual
     * @returns {Promise} Lista paginada de módulos activos
     */
    async getApplicationModules(params = { per_page: 20 }) {
        const response = await api.get("/application-modules-by-user", { params });
        return response.data;
    },

    /**
     * Obtiene información completa de la aplicación del usuario
     * Incluye plan asociado y fechas de suscripción
     * @returns {Promise} Datos de la aplicación
     */
    async getApplicationByUser() {
        const response = await api.get("/applications-by-user");
        return response.data;
    },

    /**
     * Obtiene información completa del usuario autenticado
     * @returns {Promise} Datos del usuario, roles y permisos
     */
    async getUserInfo() {
        const response = await api.get("/user");
        return response.data;
    },

    /**
     * Obtiene el catálogo de planes disponibles
     * @returns {Promise} Lista de todos los planes con sus módulos
     */
    async getPlans() {
        const response = await api.get("/plans");
        return response.data;
    },

    /**
     * Obtiene detalles de un plan específico por código
     * @param {String} code - Código del plan (esencial, profesional, clinica)
     * @returns {Promise} Detalles del plan
     */
    async getPlanByCode(code) {
        const response = await api.get(`/plans/${code}`);
        return response.data;
    },

    /**
     * Inicia período de prueba para un plan
     * @param {Object} data - Datos de la suscripción
     * @param {String} data.plan_code - Código del plan
     * @param {String} data.billing_cycle - Ciclo de facturación (monthly/yearly)
     * @returns {Promise} Respuesta de la API
     */
    async startTrial(data) {
        const response = await api.post("/plan-subscriptions/start-trial", data);
        return response.data;
    },

    /**
     * Suscribe al usuario a un plan
     * @param {Object} data - Datos de la suscripción
     * @param {String} data.plan_code - Código del plan
     * @param {String} data.billing_cycle - Ciclo de facturación (monthly/yearly)
     * @param {String} data.token_id - Token de Conekta (opcional si está en trial)
     * @returns {Promise} Respuesta de la API
     */
    async subscribe(data) {
        const response = await api.post("/plan-subscriptions", data);
        return response.data;
    },

    /**
     * Cancela la suscripción actual del usuario
     * @param {Object} data - Datos de cancelación
     * @param {String} data.reason - Motivo de la cancelación (opcional)
     * @param {Boolean} data.cancel_immediately - Si se cancela inmediatamente o al final del período (default: false)
     * @returns {Promise} Respuesta de la API
     */
    async cancelSubscription(data = {}) {
        const payload = { ...data };
        if (payload.immediate !== undefined && payload.cancel_immediately === undefined) {
            payload.cancel_immediately = payload.immediate;
            delete payload.immediate;
        }
        const response = await api.post("/plan-subscriptions/cancel", payload);
        return response.data;
    },

    /**
     * Obtiene todos los módulos del sistema
     * @returns {Promise} Lista de módulos
     */
    async getAllModules() {
        const response = await api.get("/modules");
        return response.data;
    },

    /**
     * Obtiene el historial de pagos del usuario
     * @param {Object} params - Parámetros de búsqueda
     * @param {Number} params.per_page - Resultados por página
     * @param {Number} params.page - Página actual
     * @returns {Promise} Lista de pagos realizados
     */
    async getPaymentHistory(params = { per_page: 20, page: 1 }) {
        const response = await api.get("/plan-subscriptions/payment-history", { params });
        // El backend devuelve {data: {payments: [...], message: "..."}}
        return response.data.data || response.data;
    },

    /**
   * Actualiza el método de pago para suscripciones activas
   * @param {Object} data - Datos del nuevo método de pago
   * @param {String} data.token_id - Token de Conekta del método de pago
   * @returns {Promise} Respuesta de la API
   */
  async updatePaymentMethod(data) {
    const response = await api.put("/plan-subscriptions/payment-method", data);
    return response.data;
  },

  /**
   * Agrega método de pago y/o paga manualmente (para trial/expired)
   * @param {Object} data - Datos del método de pago
   * @param {String} data.token_id - Token de Conekta del método de pago
   * @param {Boolean} data.pay_now - Si debe procesar el pago inmediatamente
   * @returns {Promise} Respuesta de la API
   */
  async paySubscriptionManually(data = {}) {
    const response = await api.post("/plan-subscriptions/payment-method", data);
    return response.data;
  },

    /**
     * Cambia el ciclo de facturación (mensual/anual)
     * @param {Object} data - Datos del ciclo de facturación
     * @param {String} data.billing_cycle - 'monthly' o 'yearly'
     * @param {String} data.plan_code - Código del plan actual
     * @returns {Promise} Respuesta de la API
     */
    async changeBillingCycle(data) {
        const response = await api.put("/plan-subscriptions/change-plan", data);
        return response.data;
    },

    /**
     * Cambia el plan actual
     * @param {Object} data - Datos del nuevo plan
     * @param {String} data.plan_code - Código del nuevo plan
     * @param {String} data.billing_cycle - Ciclo de facturación (monthly/yearly)
     * @returns {Promise} Respuesta de la API
     */
    async changePlan(data) {
        const response = await api.put("/plan-subscriptions/change-plan", data);
    return response.data;
  },

  /**
   * Reactiva una suscripción cancelada (si no ha expirado)
   * @returns {Promise} Respuesta de la API
   */
  async reactivateSubscription() {
    const response = await api.post("/plan-subscriptions/reactivate");
    return response.data;
  },
};